import posthog from 'posthog-js';

import { getQclAttributionProperties } from './qclAttribution';
import { getTelemetryIdentity } from './telemetryIdentity';

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_API_KEY || '';
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';
const SITE_LABEL = 'platform';
const PRESENCE_HEARTBEAT_INTERVAL_MS = 30_000;
const SCROLL_DEPTH_THRESHOLDS = [25, 50, 75, 90, 100] as const;

const EXCLUDED_PATH_PREFIXES = ['/admin', '/internal', '/top5', '/dailypulseold'];
type UserStatus = 'anonymous' | 'free' | 'premium';
type AnalyticsUserContext = {
  userStatus?: UserStatus;
  isPremium?: boolean;
  isAuthenticated?: boolean;
};
type PageState = {
  pathname: string;
  search: string;
  context?: AnalyticsUserContext;
};
type ScrollDepthState = {
  pageKey: string;
  maxPercent: number;
  firedThresholds: Set<number>;
};
type PendingIdentifyState = {
  email: string;
  userStatus: UserStatus;
  isPremium: boolean;
  properties: Record<string, unknown>;
};

let isInitialized = false;
let hasWarnedMissingKey = false;
let pendingRoutePath: string | null = null;
let pendingPageView: PageState | null = null;
let pendingUserContext: AnalyticsUserContext = {
  userStatus: 'anonymous',
  isPremium: false,
  isAuthenticated: false,
};
let pendingIdentifyState: PendingIdentifyState | null = null;
let activePageState: PageState | null = null;
let heartbeatIntervalId: number | null = null;
let scrollTrackingInitialized = false;
let scrollRafId: number | null = null;
let scrollDepthState: ScrollDepthState | null = null;
let pendingTelemetryIdentity = {
  anonId: null as string | null,
  sessionId: null as string | null,
  site: SITE_LABEL,
};

const matchesPrefix = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

export const isAnalyticsExcludedPath = (pathname: string): boolean =>
  EXCLUDED_PATH_PREFIXES.some((prefix) => matchesPrefix(pathname, prefix));

const resolveStatus = (context: AnalyticsUserContext = {}): UserStatus => {
  if (context.userStatus) {
    return context.userStatus;
  }

  if (context.isPremium) {
    return 'premium';
  }

  if (context.isAuthenticated) {
    return 'free';
  }

  return 'anonymous';
};

const normalizeUserContext = (context: AnalyticsUserContext = {}): AnalyticsUserContext => {
  const userStatus = resolveStatus(context);
  return {
    userStatus,
    isPremium: userStatus === 'premium',
    isAuthenticated: userStatus !== 'anonymous',
  };
};

const resolveTelemetryContext = (): {
  anonId: string | null;
  sessionId: string | null;
  site: string;
} => {
  if (typeof window === 'undefined') {
    return pendingTelemetryIdentity;
  }

  pendingTelemetryIdentity = getTelemetryIdentity();
  return pendingTelemetryIdentity;
};

const resolveEventContext = (
  context: AnalyticsUserContext = {}
): {
  anon_id: string | null;
  [key: string]: string | boolean | null;
  is_premium: boolean;
  session_id: string | null;
  site: string;
  user_status: UserStatus;
} => {
  const normalized = resolveMergedUserContext(context);
  const telemetry = resolveTelemetryContext();
  const userStatus = resolveStatus(normalized);

  return {
    anon_id: telemetry.anonId,
    session_id: telemetry.sessionId,
    site: telemetry.site || SITE_LABEL,
    user_status: userStatus,
    is_premium: userStatus === 'premium',
    ...getQclAttributionProperties(),
  };
};

const applyUserContext = (context: AnalyticsUserContext = {}): void => {
  const normalized = normalizeUserContext(context);
  const telemetry = resolveTelemetryContext();
  pendingUserContext = normalized;

  if (!hasPostHog()) {
    return;
  }

  posthog.register({
    site: telemetry.site || SITE_LABEL,
    anon_id: telemetry.anonId,
    session_id: telemetry.sessionId,
    user_status: normalized.userStatus,
    is_premium: normalized.userStatus === 'premium',
    ...getQclAttributionProperties(),
  });
};

const flushPendingIdentify = (): void => {
  if (!hasPostHog() || !pendingIdentifyState) {
    return;
  }

  const { email, userStatus, isPremium, properties } = pendingIdentifyState;
  const telemetry = resolveTelemetryContext();
  pendingIdentifyState = null;

  posthog.identify(email, {
    email,
    site: telemetry.site || SITE_LABEL,
    anon_id: telemetry.anonId,
    session_id: telemetry.sessionId,
    user_status: userStatus,
    is_premium: isPremium,
    ...properties,
  });
};

const applyRouteCaptureState = (pathname: string): void => {
  if (isAnalyticsExcludedPath(pathname)) {
    posthog.opt_out_capturing();
    return;
  }

  posthog.opt_in_capturing();
};

const resolveMergedUserContext = (context: AnalyticsUserContext = {}): AnalyticsUserContext =>
  normalizeUserContext({
    ...pendingUserContext,
    ...context,
  });

const emitPresenceHeartbeat = (pageState: PageState): void => {
  if (typeof document === 'undefined') {
    return;
  }

  if (document.visibilityState !== 'visible') {
    return;
  }

  if (isAnalyticsExcludedPath(pageState.pathname)) {
    return;
  }

  const eventContext = resolveEventContext(pageState.context);
  posthog.capture('page_presence_heartbeat', {
    path: pageState.pathname,
    query: pageState.search,
    url: window.location.href,
    title: document.title,
    heartbeat_interval_ms: PRESENCE_HEARTBEAT_INTERVAL_MS,
    ...eventContext,
  });
};

const buildPageKey = (pageState: PageState): string => `${pageState.pathname}${pageState.search}`;

const readScrollMetrics = (): {
  percent: number;
  scrollY: number;
  viewportHeight: number;
  documentHeight: number;
} | null => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return null;
  }

  const documentElement = document.documentElement;
  const body = document.body;
  const scrollY = window.scrollY || documentElement.scrollTop || body?.scrollTop || 0;
  const viewportHeight = window.innerHeight || documentElement.clientHeight || 0;
  const documentHeight = Math.max(
    body?.scrollHeight || 0,
    documentElement.scrollHeight || 0,
    body?.offsetHeight || 0,
    documentElement.offsetHeight || 0,
    body?.clientHeight || 0,
    documentElement.clientHeight || 0
  );

  if (!viewportHeight || !documentHeight) {
    return null;
  }

  const percent =
    documentHeight <= viewportHeight
      ? 100
      : Math.min(100, Math.max(0, Math.round(((scrollY + viewportHeight) / documentHeight) * 100)));

  return {
    percent,
    scrollY: Math.round(scrollY),
    viewportHeight: Math.round(viewportHeight),
    documentHeight: Math.round(documentHeight),
  };
};

const emitScrollDepth = (pageState: PageState): void => {
  if (!hasPostHog() || typeof document === 'undefined') {
    return;
  }

  if (document.visibilityState !== 'visible' || isAnalyticsExcludedPath(pageState.pathname)) {
    return;
  }

  const metrics = readScrollMetrics();
  if (!metrics) {
    return;
  }

  const pageKey = buildPageKey(pageState);
  if (!scrollDepthState || scrollDepthState.pageKey !== pageKey) {
    scrollDepthState = {
      pageKey,
      maxPercent: 0,
      firedThresholds: new Set<number>(),
    };
  }

  scrollDepthState.maxPercent = Math.max(scrollDepthState.maxPercent, metrics.percent);
  const eventContext = resolveEventContext(pageState.context);

  for (const threshold of SCROLL_DEPTH_THRESHOLDS) {
    if (metrics.percent < threshold || scrollDepthState.firedThresholds.has(threshold)) {
      continue;
    }

    scrollDepthState.firedThresholds.add(threshold);
    posthog.capture('page_scroll_depth_reached', {
      path: pageState.pathname,
      query: pageState.search,
      url: window.location.href,
      title: document.title,
      scroll_depth_threshold: threshold,
      scroll_depth_percent: metrics.percent,
      max_scroll_depth_percent: scrollDepthState.maxPercent,
      scroll_y: metrics.scrollY,
      viewport_height: metrics.viewportHeight,
      document_height: metrics.documentHeight,
      ...eventContext,
    });
  }
};

const scheduleScrollDepthCapture = (): void => {
  if (typeof window === 'undefined' || scrollRafId !== null) {
    return;
  }

  scrollRafId = window.requestAnimationFrame(() => {
    scrollRafId = null;
    if (!activePageState) {
      return;
    }

    emitScrollDepth(activePageState);
  });
};

const resetScrollDepthState = (pageState: PageState): void => {
  scrollDepthState = {
    pageKey: buildPageKey(pageState),
    maxPercent: 0,
    firedThresholds: new Set<number>(),
  };
  scheduleScrollDepthCapture();
};

const ensurePresenceHeartbeat = (): void => {
  if (typeof window === 'undefined' || heartbeatIntervalId !== null) {
    return;
  }

  heartbeatIntervalId = window.setInterval(() => {
    if (!activePageState || !hasPostHog()) {
      return;
    }

    emitPresenceHeartbeat(activePageState);
  }, PRESENCE_HEARTBEAT_INTERVAL_MS);
};

const ensureScrollDepthTracking = (): void => {
  if (typeof window === 'undefined' || scrollTrackingInitialized) {
    return;
  }

  scrollTrackingInitialized = true;
  window.addEventListener('scroll', scheduleScrollDepthCapture, { passive: true });
  window.addEventListener('resize', scheduleScrollDepthCapture);
};

const emitPageView = (pathname: string, search: string, context: AnalyticsUserContext = {}): void => {
  const eventContext = resolveEventContext(context);
  posthog.capture('$pageview', {
    path: pathname,
    query: search,
    url: window.location.href,
    title: document.title,
    ...eventContext,
  });

  if (matchesPrefix(pathname, '/dashboards')) {
    posthog.capture('dashboard_viewed', {
      dashboard_path: pathname,
      ...eventContext,
    });
  }
};

export const initAnalytics = (): void => {
  if (typeof window === 'undefined' || isInitialized) {
    return;
  }

  if (!POSTHOG_KEY) {
    if (!hasWarnedMissingKey) {
      hasWarnedMissingKey = true;
      console.warn('PostHog is disabled because REACT_APP_POSTHOG_API_KEY is not set.');
    }
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    autocapture: true,
    capture_pageview: false,
    person_profiles: 'identified_only',
  });

  isInitialized = true;
  applyUserContext(pendingUserContext);
  flushPendingIdentify();

  if (pendingRoutePath) {
    applyRouteCaptureState(pendingRoutePath);
  }

  ensurePresenceHeartbeat();
  ensureScrollDepthTracking();

  if (typeof document !== 'undefined') {
    const emitPresenceForActivePage = (): void => {
      if (!activePageState || !hasPostHog()) {
        return;
      }

      emitPresenceHeartbeat(activePageState);
    };

    document.addEventListener('visibilitychange', emitPresenceForActivePage);
    window.addEventListener('focus', emitPresenceForActivePage);
  }

  if (pendingPageView && !isAnalyticsExcludedPath(pendingPageView.pathname)) {
    emitPageView(pendingPageView.pathname, pendingPageView.search, pendingPageView.context);
    activePageState = pendingPageView;
    emitPresenceHeartbeat(pendingPageView);
    resetScrollDepthState(pendingPageView);
  }
};

const hasPostHog = (): boolean => isInitialized && typeof window !== 'undefined';

export const syncAnalyticsCaptureForRoute = (pathname: string): void => {
  pendingRoutePath = pathname;

  if (!hasPostHog()) {
    return;
  }

  applyRouteCaptureState(pathname);
};

export const capturePageView = (
  pathname: string,
  search: string,
  context: AnalyticsUserContext = {}
): void => {
  pendingPageView = { pathname, search, context };
  activePageState = pendingPageView;

  if (isAnalyticsExcludedPath(pathname)) {
    activePageState = null;
    return;
  }

  if (!hasPostHog()) {
    return;
  }

  emitPageView(pathname, search, context);
  emitPresenceHeartbeat(pendingPageView);
  resetScrollDepthState(pendingPageView);
};

export const captureEvent = (eventName: string, properties: Record<string, unknown> = {}): void => {
  if (!hasPostHog()) {
    return;
  }

  posthog.capture(eventName, {
    ...resolveEventContext(pendingUserContext),
    ...properties,
  });
};

export const identifyAnalyticsUser = (
  email: string,
  properties: Record<string, unknown> = {}
): void => {
  const isPremium =
    properties.is_premium === true || properties.isPremium === true;
  const userStatus: UserStatus = isPremium ? 'premium' : 'free';

  pendingIdentifyState = {
    email,
    userStatus,
    isPremium,
    properties,
  };

  applyUserContext({
    userStatus,
    isPremium,
    isAuthenticated: true,
  });

  flushPendingIdentify();
};

export const resetAnalyticsUser = (): void => {
  pendingIdentifyState = null;

  if (hasPostHog()) {
    posthog.reset();
  }

  applyUserContext({
    userStatus: 'anonymous',
    isPremium: false,
    isAuthenticated: false,
  });
};
